"use client";

import React from "react";
import { Sparkles, Check, ArrowRight, ShieldCheck, Droplets } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wpQueryParams = {
  search: "Kit",
  per_page: 1
};
const wpQuery = new WP_Query(wpQueryParams);
const ritualItems = [{
  step: "01. Rostro",
  title: "Crema Facial Maitake",
  desc: "Hidratación profunda y antioxidantes botánicos para un cutis iluminado."
}, {
  step: "02. Cuerpo",
  title: "Crema Corporal Reishi",
  desc: "Nutrición calmante y textura sedosa que reconforta la barrera cutánea."
}, {
  step: "03. Interior",
  title: "Té Botánico Shiitake",
  desc: "Infusión reconfortante para acompañar tu pausa diaria y digestión."
}, {
  step: "04. Bienestar",
  title: "Tintura de Hongos Funcionales",
  desc: "Extracto puro en gotas para armonizar tu vitalidad desde dentro."
}];
function FullKitConsumer() {
  const {
    products,
    loading
  } = useEcommerceProducts();
  const product = products && products.length > 0 ? products[0] : null;
  return <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-secondary/60 p-6 md:p-10 lg:p-14 shadow-sm transition-all duration-300 hover:shadow-md">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
        {/* Left Column: Image Media */}
        <div className="lg:col-span-6">
          <div className="group relative overflow-hidden rounded-2xl bg-card border border-border">
            <div className="aspect-4/3 sm:aspect-5/4 lg:aspect-square w-full overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/kit-homepage.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/kit-homepage.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/kit-homepage.png-wvc-sizes" alt="an arrangement of Spora's four products (amber jar, dropper bottle, black facial cream jar, amber body cream jar) staged on stone pedestals in a sunlit forest setting with the corresponding mushrooms beside each product" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" loading="lazy" decoding="async" />
            </div>
            <div className="absolute top-4 left-4">
              <Badge className="bg-accent text-accent-foreground border-accent-foreground/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full shadow-xs">
                Ritual integral
              </Badge>
            </div>
          </div>
        </div>

        {/* Right Column: Product Details & Purchase Action */}
        <div className="flex flex-col justify-center lg:col-span-6">
          <h2 className="font-serif text-3xl font-medium tracking-tight text-foreground sm:text-4xl">
            {product ? product.title : "Full Kit Spora"}
          </h2>

          <p className="mt-3 text-base text-foreground/85 leading-relaxed">
            Una experiencia completa para nutrir tu piel y cultivar el bienestar desde el interior. Cuatro formulaciones artesanales con extractos puros de hongos funcionales.
          </p>

          {/* Ritual Itemized Breakdown */}
          <div className="mt-6 space-y-3 border-y border-border/80 py-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Incluye en tu ritual diario:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ritualItems.map((item, i) => <div key={item.step} data-index={i} className="flex items-start gap-2.5">
                  <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                    <Check className="size-3" />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-foreground">
                      {item.title}
                    </span>
                    <span className="block text-[11px] text-foreground/75 leading-tight">
                      {item.desc}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Pricing Row */}
          <div className="mt-6 flex flex-wrap items-baseline gap-3">
            {product ? <ProductPrice product={product} className="text-3xl font-semibold text-foreground sm:text-4xl" /> : <span className="text-3xl font-semibold text-foreground sm:text-4xl">
                $2,930 MXN
              </span>}
          </div>

          {/* Action Row */}
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
            {product ? <AddToCartButton product={product} productId={product.id} className="w-full sm:w-auto" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 rounded-full text-base font-semibold shadow-sm transition-all" text="Comprar kit completo" showQuantity={false} /> : <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-base font-semibold shadow-sm">
                <Link to="/ShopPage">
                  Comprar kit completo
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>}

            {product && product.link ? <Button asChild variant="outline" className="rounded-full border-border bg-card/80 px-6 py-6 text-sm font-semibold text-foreground hover:bg-accent hover:text-accent-foreground">
                <Link to={product.link}>Ver detalles del ritual</Link>
              </Button> : <Button asChild variant="outline" className="rounded-full border-border bg-card/80 px-6 py-6 text-sm font-semibold text-foreground hover:bg-accent hover:text-accent-foreground">
                <Link to="/ShopPage">Explorar catálogo</Link>
              </Button>}
          </div>

          {/* Trust Guarantees */}
          <div className="mt-6 flex flex-wrap items-center gap-6 pt-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-primary" />
              Ingredientes 100% orgánicos certificados
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Droplets className="size-4 text-primary" />
              Libre de parabenos y sulfatos
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export default function FullKit() {
  return <section id="full-kit-destacado" data-nav="light" className="w-full bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-14">
        {/* Section Header */}
        <div className="mb-10 text-center md:mb-14 max-w-2xl mx-auto">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-accent/70 px-3.5 py-1 text-xs font-semibold uppercase tracking-widest text-accent-foreground">
            <Sparkles className="size-3.5 text-primary" />
            Edición especial
          </div>
          <h2 className="font-serif text-3xl font-medium tracking-tight text-foreground sm:text-4xl md:text-5xl">
            El ritual integral en un solo kit
          </h2>
          <p className="mt-3 text-base text-muted-foreground leading-relaxed">
            Una sinergia formulada para armonizar piel y cuerpo con el poder botánico de los hongos medicinales.
          </p>
        </div>

        {/* Dynamic WooCommerce Provider Wrapper */}
        <EcommerceProductsProvider wp_query={{
        search: "Kit",
        per_page: 1
      }}>
          <FullKitConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}